import { ATTENDANCE_FILTERS } from "constants";

export const DASHBOARD_CARDS = [
  {
    name: "total_users",
    text: "All",
    filterBy: ATTENDANCE_FILTERS.all,
    iconColor: "info",
    iconComponent: "group",
  },
  {
    name: "total_checked_in_office",
    text: "Checked In (Office)",
    filterBy: ATTENDANCE_FILTERS.checked_in_office,
    iconColor: "success",
    iconComponent: "login",
  },
  {
    name: "total_checked_in_wfh",
    text: "Checked In (WFH)",
    filterBy: ATTENDANCE_FILTERS.checked_in_wfh,
    iconColor: "primary",
    iconComponent: "home_work",
  },
  {
    name: "total_on_break",
    text: "On break",
    filterBy: ATTENDANCE_FILTERS.on_break,
    iconColor: "warning",
    iconComponent: "cookie",
  },
  {
    name: "total_checked_out",
    text: "Checked Out",
    filterBy: ATTENDANCE_FILTERS.checked_out,
    iconColor: "dark",
    iconComponent: "logout",
  },
  {
    name: "total_absent",
    text: "Absent",
    filterBy: ATTENDANCE_FILTERS.absent,
    iconColor: "error",
    iconComponent: "event_busy",
  },
];

export const USER_REPORT_CARDS = [
  {
    name: "total_hours",
    text: "Total Hours",
    iconColor: "info",
    iconComponent: "alarm_clock",
  },
  {
    name: "total_attendance_hours",
    text: "Total Attendance",
    iconColor: "success",
    iconComponent: "login",
  },
  {
    name: "office_duration_hours",
    text: "Office Duration",
    iconColor: "primary",
    iconComponent: "work",
  },
  {
    name: "breaks_duration",
    text: "Break Duration",
    iconColor: "warning",
    iconComponent: "cookie",
  },
  {
    name: "wfh_duration_hours",
    text: "Work From Home Duration",
    iconColor: "dark",
    iconComponent: "home",
  },
];

export const THEME_COLORS = {
  primary: "#18C1E8",
  secondary: "#9825B9",
  secondary_light: "#DADADA40",
  black: "#000000",
  white: "#ffffff",
};

export const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    width: "135px",
    fontSize: 12,
    minHeight: "15px",
    height: "35px",
    textAlign: "center",
    border: "none",
    boxShadow: "none",
  }),
  indicatorSeparator: (state) => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: THEME_COLORS.black,
  }),
  option: (provided, state) => {
    const selectedOptionColor = THEME_COLORS.white;
    const selectedOptionBackgroundColor = THEME_COLORS.primary;
    const selectedOptionColorLight = THEME_COLORS.black;
    const selectedOptionBackgroundColorLight = THEME_COLORS.secondary_light;

    return {
      ...provided,
      height: 35,
      fontSize: 12,
      color: state.isSelected && selectedOptionColor,
      backgroundColor: state.isSelected && selectedOptionBackgroundColor,
      "&:hover": {
        color: state.isSelected ? selectedOptionColor : selectedOptionColorLight,
        backgroundColor: state.isSelected
          ? selectedOptionBackgroundColor
          : selectedOptionBackgroundColorLight,
      },
      "&:focus": {
        color: state.isSelected ? selectedOptionColor : selectedOptionColorLight,
        backgroundColor: state.isSelected
          ? selectedOptionBackgroundColor
          : selectedOptionBackgroundColorLight,
      },
      "&:active": {
        color: state.isSelected ? selectedOptionColor : selectedOptionColorLight,
        backgroundColor: state.isSelected
          ? selectedOptionBackgroundColor
          : selectedOptionBackgroundColorLight,
      },
    };
  },
};

export const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 300,
  bgcolor: "background.paper",
  borderRadius: 4,
  p: 4,
};

// The red "(3 not approved)" after a card's total. The per-status split lives in
// the chips above the table. null hides it (other cards, no data yet, nobody
// unapproved, or a weekend / public holiday).
const CARD_BREAKDOWN_KEYS = {
  total_checked_in_wfh: "wfh_breakdown",
  total_absent: "absent_breakdown",
};

export const getCardNotApproved = (cardName, cards) => {
  const breakdown = cards[CARD_BREAKDOWN_KEYS[cardName]];
  if (cards.is_day_off || !breakdown || !breakdown.without_approval) return null;
  return `(${breakdown.without_approval} not approved)`;
};

// Chips above the table when the WFH or Absent card is selected. `value` is what
// the API's `approval` param takes ("" = everyone on the card); the labels match
// the tag shown on each row.
const CARD_APPROVAL_CHIPS = {
  total_checked_in_wfh: {
    breakdown: "wfh_breakdown",
    chips: [
      { value: "approved", label: "WFH approved", key: "approved", color: "success" },
      { value: "remote", label: "Remote", key: "remote", color: "info" },
      { value: "office_hours", label: "Worked in office", key: "office_hours", color: "success" },
      { value: "unapproved", label: "No approval", key: "without_approval", color: "error" },
    ],
  },
  total_absent: {
    breakdown: "absent_breakdown",
    chips: [
      { value: "on_leave", label: "On approved leave", key: "on_leave", color: "info" },
      { value: "pending", label: "Request pending", key: "pending", color: "warning" },
      { value: "unapproved", label: "No approval", key: "without_approval", color: "error" },
    ],
  },
};

// null when the selected card has no chips (or there is no breakdown to count).
export const getApprovalChips = (cardName, cards) => {
  const config = CARD_APPROVAL_CHIPS[cardName];
  const breakdown = config && !cards.is_day_off && cards[config.breakdown];
  if (!breakdown) return null;
  const chips = config.chips.map(({ key, ...chip }) => ({ ...chip, count: breakdown[key] }));
  const total = chips.reduce((sum, chip) => sum + chip.count, 0);
  return [{ value: "", label: "All", count: total, color: "secondary" }, ...chips];
};

// Tag under the status badge. A row has either an absence_status (absent) or a
// wfh_status (checked in from home); "remote" needs none, the Remote tag by the
// name already says it.
export const APPROVAL_TAGS = {
  on_leave: { text: "On approved leave", color: "info" },
  pending: { text: "Request pending", color: "warning" },
  unapproved: { text: "No approval", color: "error" },
  approved: { text: "WFH approved", color: "success" },
  office_hours: { text: "Worked in office", color: "success" },
};
